<template>
  <v-card flat>
    <v-container fluid>
      <!-- Tract Activities -->
      <v-row v-if="tractActivities.length > 0">
        <v-col cols="12">
          <span class="title">
            {{$t('tractReceivables')}}
          </span>
        </v-col>
        <v-col cols="12" class="mt-n6">
          <DataTable
          :items="tractActivities"
          :headers="headers"
          :propDense="true"
          :hideHeader="true"
          :customCells="customReceivableCells"
          :actions="[]"
          >
            <template #weight="slotProps">
              {{ tonStringFromPounds(slotProps.item.weight, 3, isMetric) }}
            </template>
            <template #volume="{item}">
              {{ mbfStringFromBf(item.volume) }}
            </template>
            <template #amount="{item}">
              <span>
                {{ formatMoney(item.amount) }}
              </span>
            </template>
            <template #footer>
              <tr></tr>
            </template>
          </DataTable>
        </v-col>
      </v-row>

      <!-- Deck Activities (Log Yard Sale) -->
      <v-row v-if="deckActivities.length > 0">
        <v-col cols="12">
          <span class="title">
            {{$t('logYardSaleReceivables')}}
          </span>
        </v-col>
        <v-col cols="12" class="mt-n6">
          <DataTable
          :items="deckActivities"
          :headers="deckActivityHeaders"
          :propDense="true"
          :hideHeader="true"
          :customCells="customReceivableCells"
          :actions="[]"
          >
            <template #weight="slotProps">
              {{ tonStringFromPounds(slotProps.item.weight, 3, isMetric) }}
            </template>
            <template #amount="{item}">
              <span>
                {{ formatMoney(item.amount) }}
              </span>
            </template>
            <template #footer>
              <tr></tr>
            </template>
          </DataTable>
        </v-col>
      </v-row>

      <!-- Receivables (BP) -->
      <v-row v-if="byproductReceivables.length > 0">
        <v-col cols="12">
          <span class="title">
            {{$t('receivables')}}
          </span>
        </v-col>
        <v-col cols="12" class="mt-n6">
          <DataTable
          :items="byproductReceivables"
          :headers="headers"
          :propDense="true"
          :hideHeader="true"
          :customCells="customReceivableCells"
          :actions="[]"
          >
            <template #weight="slotProps">
              {{ tonStringFromPounds(slotProps.item.weight, 3, isMetric) }}
            </template>
            <template #amount="{item}">
              <span>
                {{ formatMoney(item.amount) }}
              </span>
            </template>
          </DataTable>
        </v-col>
      </v-row>

      <!-- Corrections -->
      <v-row v-if="receivableRegister.correctionFinancials.length > 0">
        <v-col cols="12">
          <span class="title">
            {{$t('corrections')}}
          </span>
        </v-col>
        <v-col cols="12" class="mt-n6">
          <DataTable
            :items.sync="receivableRegister.correctionFinancials"
            :headers="correctionHeaders"
            :propDense="true"
            :hideHeader="true"
            :customCells="customCorrectionCells"
            :actions="[]"
          >
            <template #activity="{item}">
              <Icon
              margin="mt-n1 mr-1"
              icon="mdi-auto-fix"
              dataTestId="rec-reg-corrections"
              :small="true"
              :iconColor="item.correctionSide === 0 ? 'error' : 'success'"
              :tooltipColor="item.correctionSide === 0 ? 'error' : 'success'"
              :tooltipText="getCorrectionTooltipText(item)"
              />
              <span>
                {{item.activity}}
              </span>
            </template>
            <template #net-weight="{item}">
              {{tonStringFromPounds(item.netWeight, 3, isMetric)}}
            </template>
            <template #amount="{item}">
              {{getAmountText(item) }}
            </template>
            <template #body.append>
              <tr class="font-weight-bold">
                <td v-for="(_, i) in Array.from({length: correctionHeaders.length - 1})" :key="`${receivableRegister.accountName}-${i}`"/>
                <td class="text-right">{{totalCorrected}}</td>
              </tr>
            </template>
          </DataTable>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { tonStringFromPounds, formatMoney, mbfStringFromBf } from '@/utils/NumericMutations'
import { receivableRegisterHeaders, prCorrectionHeaders } from '@/headers/Cycles.js'
import { CorrectionTypes, CorrectionSide } from '@/utils/Enumerations.js'
import { customReceivableCells, customCorrectionCells } from './CustomReceivableRegisterCells.js'
import { mapGetters } from 'vuex'
export default {
  name: 'ReceivableRegisterCard',

  props: {
    receivableRegister: {
      type: Object,
      required: true
    },
    isByproduct: {
      type: Boolean,
      required: true
    }
  },

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue')
  },

  data: () => ({
    customReceivableCells,
    customCorrectionCells
  }),

  computed: {
    ...mapGetters('settlements', ['useMetricTons', 'useByproductMetricTons']),
    headers () {
      const containsVolumeBasedActivities = this.receivableRegister.activities.some(a => a.volume > 0)
      return receivableRegisterHeaders(this.isByproduct, false, containsVolumeBasedActivities)
    },

    deckActivityHeaders () {
      return receivableRegisterHeaders(true, true)
    },

    isMetric () {
      return this.isByproduct ? this.useByproductMetricTons : this.useMetricTons
    },

    correctionHeaders () {
      return prCorrectionHeaders({ isByproduct: this.isByproduct })
    },

    tractActivities () { // TODO: IH - Handle with Tract Sourced BPP
      return this.receivableRegister.activities.filter(activity => activity.tractId !== 0)
    },

    deckActivities () {
      return this.receivableRegister.activities.filter(activity => activity.fromDeckId !== 0)
    },

    totalCorrected () {
      return formatMoney(this.receivableRegister.correctionFinancials.reduce((total, curr) => total + curr.amount, 0))
    },

    byproductReceivables () {
      return this.isByproduct ? this.receivableRegister.activities : []
    }
  },

  methods: {
    tonStringFromPounds,
    formatMoney,
    mbfStringFromBf,

    getPayoutString (item) {
      const { payout, grossPayout } = item
      if (payout === grossPayout) {
        return formatMoney(payout)
      } else {
        return `${formatMoney(payout)} (${formatMoney(grossPayout)})`
      }
    },

    getCorrectionTooltipText ({ correctionType, correctionSide }) {
      return `${CorrectionTypes.find(ct => ct.value === correctionType).name}
        ======
        ${CorrectionSide.find(cs => cs.value === correctionSide).name}`
    },

    getAmountText ({ amount }) {
      return formatMoney(amount)
    }
  }
}
</script>
